import { Trash2, PackagePlus, PenLine } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { inr } from "@/lib/api";
import { GST_RATES, blankItem } from "@/lib/quote";

const Num = ({ label, value, onChange, disabled, testid, step = "any" }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
    <Input type="number" min="0" step={step} inputMode="decimal" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="font-mono-num h-9" data-testid={testid} />
  </div>
);

const ItemRow = ({ it, i, line, set, remove, disabled }) => (
  <div className="rounded-xl border bg-white p-3 space-y-2" data-testid={`quote-item-${i}`}>
    <div className="flex items-start gap-2">
      <span className="h-6 w-6 shrink-0 rounded-full bg-slate-900 text-white text-[11px] font-bold grid place-items-center mt-1.5">{i + 1}</span>
      <div className="flex-1 grid gap-2">
        <Input value={it.name} disabled={disabled} placeholder="Item name" onChange={(e) => set({ name: e.target.value })} className="font-semibold" data-testid={`quote-item-name-${i}`} />
        <Input value={it.description} disabled={disabled} placeholder="Description / model / specs" onChange={(e) => set({ description: e.target.value })} className="text-sm" data-testid={`quote-item-desc-${i}`} />
      </div>
      {!disabled && <Button type="button" variant="ghost" size="icon" onClick={remove} className="text-rose-600" data-testid={`quote-item-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
    </div>
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
      <Num label="Qty" value={it.qty} onChange={(v) => set({ qty: v })} disabled={disabled} testid={`quote-item-qty-${i}`} />
      <div><Label className="text-[10px] uppercase tracking-wider text-slate-500">Unit</Label><Input value={it.unit} disabled={disabled} onChange={(e) => set({ unit: e.target.value })} className="h-9" /></div>
      <Num label="Rate ₹" value={it.rate} onChange={(v) => set({ rate: v })} disabled={disabled} testid={`quote-item-rate-${i}`} />
      <Num label="Disc %" value={it.discount_pct} onChange={(v) => set({ discount_pct: v })} disabled={disabled} testid={`quote-item-disc-${i}`} />
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">GST %</Label>
        <Select value={String(it.gst_rate)} onValueChange={(v) => set({ gst_rate: Number(v) })} disabled={disabled}>
          <SelectTrigger className="h-9" data-testid={`quote-item-gst-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{GST_RATES.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label className="text-[10px] uppercase tracking-wider text-slate-500">HSN</Label><Input value={it.hsn} disabled={disabled} onChange={(e) => set({ hsn: e.target.value })} className="h-9" /></div>
    </div>
    <div className="flex justify-between text-xs text-slate-500 pt-1 border-t">
      <span>Taxable {inr(line?.taxable)} + GST {inr(line?.gst)}</span>
      <span className="font-mono-num font-bold text-slate-900 text-sm" data-testid={`quote-item-total-${i}`}>{inr(line?.total)}</span>
    </div>
  </div>
);

export const ItemsEditor = ({ items, lines, onChange, products, disabled }) => {
  const setAt = (i) => (patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) onChange([...items, { ...blankItem(), product_id: p.id, name: p.name, description: p.description, hsn: p.hsn, unit: p.unit, rate: p.rate, gst_rate: p.gst_rate }]);
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <ItemRow key={i} it={it} i={i} line={lines[i]} set={setAt(i)} remove={() => onChange(items.filter((_, j) => j !== i))} disabled={disabled} />
      ))}
      {items.length === 0 && <div className="text-sm text-slate-500 text-center p-5 border border-dashed rounded-xl">Add items from the catalogue or type your own.</div>}
      {!disabled && (
        <div className="grid sm:grid-cols-2 gap-2">
          <Select value="" onValueChange={addProduct}>
            <SelectTrigger className="bg-sky-50 border-sky-200 text-sky-800 font-semibold" data-testid="quote-add-catalogue-select">
              <div className="flex items-center gap-2"><PackagePlus className="h-4 w-4" />{products.length ? "Add from catalogue" : "Catalogue is empty"}</div>
            </SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {inr(p.rate)} · {p.gst_rate}%</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={() => onChange([...items, blankItem()])} data-testid="quote-add-custom-item-button">
            <PenLine className="h-4 w-4 mr-2" />Type custom item
          </Button>
        </div>
      )}
    </div>
  );
};
